import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { RstScoreBadge } from "@/components/RstScoreBadge";
import { subscribeToPush, unsubscribeFromPush, getPushStatus } from "@/lib/push";
import {
  Megaphone, Wallet, ShoppingCart, Star, User, Settings, ChevronRight,
  Package, Truck, Coins, XCircle, Bell, BellOff, BellRing,
} from "lucide-react";

const DONE = ["delivered", "closed", "cancelled", "refunded"];

const BigCard = ({ Icon, title, desc, onClick, testid, tone = "primary" }) => (
  <button onClick={onClick} data-testid={testid}
    className="group text-left rounded-3xl bg-card border border-border p-5 flex items-center gap-4 hover:border-primary hover:shadow-md transition-all active:scale-[0.99]">
    <span className={`h-14 w-14 rounded-2xl flex items-center justify-center shrink-0 ${tone === "secondary" ? "bg-secondary/10 text-secondary" : tone === "accent" ? "bg-accent/40 text-accent-foreground" : "bg-primary/10 text-primary"}`}>
      <Icon className="h-7 w-7" />
    </span>
    <div className="flex-1 min-w-0">
      <p className="font-heading font-bold text-secondary text-lg leading-tight">{title}</p>
      <p className="text-sm text-muted-foreground truncate">{desc}</p>
    </div>
    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
  </button>
);

const KpiTile = ({ Icon, label, value, tone = "primary" }) => (
  <div className="rounded-2xl bg-card border border-border p-4" data-testid={`kpi-${label}`}>
    <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${tone === "success" ? "bg-success/10 text-success" : tone === "destructive" ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
      <Icon className="h-5 w-5" />
    </span>
    <p className="mt-2.5 font-heading font-extrabold text-2xl text-foreground leading-none">{value}</p>
    <p className="text-xs text-muted-foreground mt-1">{label}</p>
  </div>
);

function PushCard() {
  const { t } = useT();
  const [status, setStatus] = useState({ supported: true, permission: "default", subscribed: false });
  const [busy, setBusy] = useState(false);

  const refresh = async () => setStatus(await getPushStatus());
  useEffect(() => { refresh(); }, []);

  const isOn = status.permission === "granted" && status.subscribed;
  const isDenied = status.permission === "denied";

  const enable = async () => {
    setBusy(true);
    const ok = await subscribeToPush(true);
    await refresh();
    setBusy(false);
    if (ok) toast.success(t("push.enabledOk"));
    else toast.error(t("push.enableFailed"));
  };

  const disable = async () => {
    setBusy(true);
    await unsubscribeFromPush();
    await refresh();
    setBusy(false);
    toast.success(t("push.disabledOk"));
  };

  const StatusIcon = isOn ? BellRing : isDenied ? BellOff : Bell;
  const statusLabel = isOn ? t("push.statusOn") : isDenied ? t("push.statusDenied") : t("push.statusOff");
  const desc = !status.supported ? t("push.descUnsupported")
    : isOn ? t("push.descOn")
    : isDenied ? t("push.descDenied")
    : t("push.descOff");
  const statusTone = isOn ? "bg-success/10 text-success" : isDenied ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary";

  return (
    <section className="mb-8 rounded-3xl bg-card border border-border p-5" data-testid="push-card">
      <div className="flex items-start gap-4">
        <span className={`h-14 w-14 rounded-2xl flex items-center justify-center shrink-0 ${statusTone}`}>
          <StatusIcon className="h-7 w-7" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-heading font-bold text-secondary text-lg leading-tight">{t("push.title")}</p>
            <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${statusTone}`} data-testid="push-status-badge">{statusLabel}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{desc}</p>
          {status.supported && !isDenied && (
            <div className="mt-4 flex gap-2 flex-wrap">
              {isOn ? (
                <button onClick={disable} disabled={busy} data-testid="push-disable-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-bold text-secondary hover:bg-muted transition-colors disabled:opacity-60">
                  <BellOff className="h-4 w-4" />{t("push.disable")}
                </button>
              ) : (
                <button onClick={enable} disabled={busy} data-testid="push-enable-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-60 active:scale-[0.99]">
                  <Bell className="h-4 w-4" />{busy ? t("push.enabling") : t("push.enable")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function MonEspace() {
  const { user, refreshUser } = useAuth();
  const brand = useBrand();
  const { t } = useT();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [listings, setListings] = useState([]);
  const isSeller = user?.role === "seller";

  useEffect(() => {
    window.scrollTo(0, 0);
    refreshUser();
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    if (isSeller) api.get("/listings/mine").then((r) => setListings(r.data)).catch(() => {});
  }, [isSeller]);

  const stats = useMemo(() => {
    const delivered = orders.filter((o) => ["delivered", "closed"].includes(o.status));
    const current = orders.filter((o) => !DONE.includes(o.status));
    const refused = orders.filter((o) => ["refused", "cancelled"].includes(o.status));
    const revenue = delivered.reduce((s, o) => s + (o.total_amount || 0) + (o.delivery_fee || 0), 0);
    return {
      total: orders.length, current: current.length, delivered: delivered.length,
      refused: refused.length, revenue,
      activeListings: listings.filter((l) => l.status === "active").length,
    };
  }, [orders, listings]);

  if (!user) return null;

  return (
    <div className="container-page py-8 pb-28 max-w-3xl" data-testid="mon-espace">
      <div className="flex items-center gap-3 mb-6">
        <span className="h-14 w-14 rounded-2xl bg-secondary/10 flex items-center justify-center shrink-0"><User className="h-7 w-7 text-secondary" /></span>
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{t("dashboard.welcome")}</p>
          <h1 className="font-heading font-extrabold text-2xl text-secondary truncate">{user.name}</h1>
        </div>
      </div>

      {/* Reputation / RST */}
      <div className="mb-6 flex items-center gap-2 flex-wrap" data-testid="mon-espace-rst">
        <RstScoreBadge score={user.rst_score} tier={user.rst_tier} size="md" />
        <span className="text-xs text-muted-foreground">{t("rst.scoreHint")}</span>
      </div>

      {/* Notifications push */}
      <PushCard />

      {/* Key numbers */}
      <section className="mb-8" data-testid="mon-espace-kpis">
        <h2 className="font-heading font-bold text-lg text-secondary mb-3">{isSeller ? t("space.salesBoard") : t("space.ordersBoard")}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {isSeller ? (
            <>
              <KpiTile Icon={Package} label={t("space.ordersReceived")} value={stats.total} />
              <KpiTile Icon={Truck} label={t("space.delivered")} value={stats.delivered} tone="success" />
              <KpiTile Icon={XCircle} label={t("space.refused")} value={stats.refused} tone="destructive" />
              <KpiTile Icon={Coins} label={t("space.revenue")} value={formatPrice(stats.revenue, brand.currencySymbol)} tone="success" />
            </>
          ) : (
            <>
              <KpiTile Icon={ShoppingCart} label={t("space.myPurchases")} value={stats.total} />
              <KpiTile Icon={Package} label={t("space.inProgress")} value={stats.current} />
              <KpiTile Icon={Truck} label={t("space.delivered")} value={stats.delivered} tone="success" />
              <KpiTile Icon={Coins} label={t("space.spend")} value={formatPrice(stats.revenue, brand.currencySymbol)} />
            </>
          )}
        </div>
      </section>

      {/* Big cards */}
      <section className="grid sm:grid-cols-2 gap-3" data-testid="mon-espace-cards">
        {isSeller ? (
          <>
            <BigCard Icon={Megaphone} tone="primary" title={t("space.myListings")} desc={t("space.myListingsDesc", { n: stats.activeListings })} onClick={() => navigate("/vendeur")} testid="card-my-listings" />
            <BigCard Icon={Wallet} tone="secondary" title={t("space.mySales")} desc={t("space.mySalesDesc")} onClick={() => navigate("/vendeur/commandes")} testid="card-my-sales" />
            <BigCard Icon={Star} tone="accent" title={t("space.reputation")} desc={t("space.reputationDesc")} onClick={() => navigate("/mon-evolution")} testid="card-reputation" />
            <BigCard Icon={User} tone="primary" title={t("space.profile")} desc={t("space.profileDesc")} onClick={() => navigate("/profil")} testid="card-profile" />
            <BigCard Icon={Settings} tone="secondary" title={t("space.settings")} desc={t("space.settingsDesc")} onClick={() => navigate("/parametres")} testid="card-settings" />
          </>
        ) : (
          <>
            <BigCard Icon={ShoppingCart} tone="primary" title={t("space.myPurchases")} desc={t("space.myPurchasesDesc")} onClick={() => navigate("/acheteur/commandes")} testid="card-my-purchases" />
            <BigCard Icon={Megaphone} tone="secondary" title={t("nav.offers")} desc={t("space.myOffersDesc")} onClick={() => navigate("/offres")} testid="card-my-offers" />
            <BigCard Icon={Star} tone="accent" title={t("space.reputation")} desc={t("space.reputationDesc")} onClick={() => navigate("/mon-evolution")} testid="card-reputation" />
            <BigCard Icon={User} tone="primary" title={t("space.profile")} desc={t("space.profileDesc")} onClick={() => navigate("/profil")} testid="card-profile" />
            <BigCard Icon={Settings} tone="secondary" title={t("space.settings")} desc={t("space.settingsDesc")} onClick={() => navigate("/parametres")} testid="card-settings" />
          </>
        )}
      </section>
    </div>
  );
}
